import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChefHat, LoaderCircle, RefreshCw, ArrowLeft, Plus, Pencil, Trash2, ImagePlus } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useMasters, PillSelect, Chips } from "../components/form/engine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const NEXT = {
  placed: { to: "cooking", label: "Start Cooking" },
  cooking: { to: "out-for-delivery", label: "Out for Delivery" },
  "out-for-delivery": { to: "delivered", label: "Mark Delivered" },
};

const STATUS_STYLE = {
  placed: "bg-[#22C55E]/10 text-[#22C55E]",
  cooking: "bg-[#F97316]/10 text-[#F97316]",
  "out-for-delivery": "bg-[#7C3AED]/15 text-[#A78BFA]",
  delivered: "bg-[#06B6D4]/10 text-[#06B6D4]",
  cancelled: "bg-white/5 text-slate-500",
};

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const EMPTY_DISH = { name: "", price: "", description: "", emoji: "", photo_path: "", active: true, food_type: "", cuisine: "", meal: "", prep_time: "" };

const MenuPanel = () => {
  const masters = useMasters();
  const [dishes, setDishes] = useState(null);
  const [form, setForm] = useState(null); // null = form closed
  const [editId, setEditId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/menu/mine`, headers());
      setDishes(data.dishes);
    } catch {
      setDishes([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm({ ...EMPTY_DISH });
    setEditId(null);
    setPhotoPreview("");
  };

  const openEdit = (d) => {
    setForm({ name: d.name, price: String(d.price), description: d.description, emoji: d.emoji, photo_path: "", active: d.active, food_type: d.food_type || "", cuisine: d.cuisine || "", meal: d.meal || "", prep_time: d.prep_time || "" });
    setEditId(d.id);
    setPhotoPreview(d.photo_url ? `${BACKEND}${d.photo_url}` : "");
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/menu/photo`, fd, headers());
      setForm((f) => ({ ...f, photo_path: data.path }));
      setPhotoPreview(`${BACKEND}${data.url}`);
      toast.success("Photo uploaded 📸");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        price: parseInt(form.price, 10) || 0,
        description: form.description,
        emoji: form.emoji,
        active: form.active,
        food_type: form.food_type || "",
        cuisine: form.cuisine || "",
        meal: form.meal || "",
        prep_time: form.prep_time || "",
        ...(form.photo_path ? { photo_path: form.photo_path } : {}),
      };
      if (editId) {
        await axios.patch(`${API}/menu/${editId}`, { ...EMPTY_DISH, ...payload, photo_path: form.photo_path || undefined }, headers());
        toast.success("Dish updated 🍽️");
      } else {
        await axios.post(`${API}/menu`, payload, headers());
        toast.success("Dish added to your menu 🎉");
      }
      setForm(null);
      setEditId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    try {
      await axios.delete(`${API}/menu/${d.id}`, headers());
      toast.success(`"${d.name}" removed`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleActive = async (d) => {
    try {
      await axios.patch(
        `${API}/menu/${d.id}`,
        { name: d.name, price: d.price, description: d.description, emoji: d.emoji, active: !d.active, food_type: d.food_type || "", cuisine: d.cuisine || "", meal: d.meal || "", prep_time: d.prep_time || "" },
        headers()
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="menu-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Dishes you add here appear live on FoodBite for every student.</p>
        <button
          data-testid="dish-add"
          onClick={openAdd}
          className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B]"
        >
          <Plus className="h-3.5 w-3.5" /> Add Dish
        </button>
      </div>

      {form && (
        <div className="mt-6 rounded-3xl border border-[#F97316]/30 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="dish-form">
          <p className="font-display text-lg font-bold">{editId ? "Edit dish" : "New dish"}</p>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              data-testid="dish-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Dish name (e.g. Paneer Tikka Wrap)"
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#F97316]/60"
            />
            <div className="flex gap-3">
              <input
                data-testid="dish-price"
                type="number"
                min="1"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="₹ Price"
                className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#F97316]/60"
              />
              <input
                data-testid="dish-emoji"
                value={form.emoji}
                onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                placeholder="🍽️"
                className="w-20 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-center text-lg outline-none focus:border-[#F97316]/60"
              />
            </div>
            <input
              data-testid="dish-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Short description (optional)"
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#F97316]/60 sm:col-span-2"
            />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="dish-masters">
            <PillSelect label="Food Type" options={masters.food_types || []} value={form.food_type} onChange={(v) => setForm({ ...form, food_type: v })} testid="dish-food-type" />
            <PillSelect label="Cuisine" options={masters.cuisines || []} value={form.cuisine} onChange={(v) => setForm({ ...form, cuisine: v })} testid="dish-cuisine" />
            <PillSelect label="Meal" options={masters.meals || []} value={form.meal} onChange={(v) => setForm({ ...form, meal: v })} testid="dish-meal" />
            <PillSelect label="Preparation Time" options={masters.prep_times || []} value={form.prep_time} onChange={(v) => setForm({ ...form, prep_time: v })} testid="dish-prep-time" />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              data-testid="dish-photo-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold text-slate-200 transition-colors hover:border-[#F97316]/60 disabled:opacity-50"
            >
              {uploading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
              {uploading ? "Uploading…" : photoPreview ? "Change photo" : "Add photo"}
            </button>
            <input ref={fileRef} data-testid="dish-photo" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pickPhoto} />
            {photoPreview && (
              <img src={photoPreview} alt="Dish" data-testid="dish-photo-preview" className="h-14 w-14 rounded-xl border border-white/10 object-cover" />
            )}
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                data-testid="dish-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="h-4 w-4 accent-[#06B6D4]"
              />
              Live on menu
            </label>
          </div>
          <div className="mt-5 flex gap-2.5">
            <button
              data-testid="dish-save"
              onClick={save}
              disabled={saving || !form.name.trim() || !form.price}
              className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-7 py-3 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
            >
              {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : editId ? "Save changes" : "Add to menu"}
            </button>
            <button
              data-testid="dish-cancel"
              onClick={() => { setForm(null); setEditId(null); }}
              className="rounded-full border border-white/15 px-6 py-3 text-xs font-bold text-slate-400 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="menu-dishes">
        {dishes === null ? (
          <LoaderCircle className="h-6 w-6 animate-spin text-[#06B6D4]" />
        ) : dishes.length === 0 ? (
          <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500 sm:col-span-2">
            No dishes yet — add your first one 👆
          </p>
        ) : (
          dishes.map((d) => (
            <div key={d.id} data-testid={`dish-${d.id.slice(-6)}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              {d.photo_url ? (
                <img src={`${BACKEND}${d.photo_url}`} alt={d.name} className="h-14 w-14 rounded-xl border border-white/10 object-cover" />
              ) : (
                <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/5 text-3xl">{d.emoji}</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{d.name}</p>
                <p className="font-jbmono text-[11px] text-[#22C55E]">₹{d.price}</p>
                {(d.cuisine || d.meal || d.food_type) && (
                  <p className="font-jbmono text-[9px] uppercase tracking-wider text-slate-500">
                    {[d.food_type, d.cuisine, d.meal, d.prep_time].filter(Boolean).join(" · ")}
                  </p>
                )}
                <button
                  data-testid={`dish-toggle-${d.id.slice(-6)}`}
                  onClick={() => toggleActive(d)}
                  className={`mt-1 rounded-full px-2.5 py-0.5 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                    d.active ? "bg-[#06B6D4]/10 text-[#06B6D4]" : "bg-white/5 text-slate-500"
                  }`}
                >
                  {d.active ? "● Live" : "○ Hidden"}
                </button>
              </div>
              <button
                data-testid={`dish-edit-${d.id.slice(-6)}`}
                onClick={() => openEdit(d)}
                className="rounded-full border border-white/10 p-2 text-slate-400 hover:border-[#7C3AED]/60 hover:text-white"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`dish-delete-${d.id.slice(-6)}`}
                onClick={() => remove(d)}
                className="rounded-full border border-white/10 p-2 text-slate-400 hover:border-[#F97316]/60 hover:text-[#F97316]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const CHEFRIDER_SLOTS = ["9 AM - 12 PM", "12 PM - 3 PM", "3 PM - 6 PM", "6 PM - 10 PM"];

const CHECKLIST = [
  { key: "id_verification", label: "ID Verification", hint: "Upload a govt ID below" },
  { key: "food_safety", label: "Food Safety Training", hint: "Watch the 20-min module" },
  { key: "funz_training", label: "FUNZ Certification", hint: "Cloud-kitchen training session" },
  { key: "kitchen_assessment", label: "Kitchen Assessment", hint: "Hygiene self-check passed" },
  { key: "skill_assessment", label: "Chef Skill Assessment", hint: "Signature dish reviewed" },
];

const CertificationCard = () => {
  const [cert, setCert] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chefrider/certification/mine`, headers());
      setCert(data.certification || { status: "draft", checklist: {}, id_doc_url: "" });
    } catch {
      setCert({ status: "draft", checklist: {}, id_doc_url: "" });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/chefrider/certification/upload-id`, fd, headers());
      toast.success("ID uploaded 🪪");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const toggleItem = async (item, done) => {
    try {
      await axios.patch(`${API}/chefrider/certification/checklist`, { item, done }, headers());
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/chefrider/certification/submit`, {}, headers());
      toast.success("Submitted for review — admin will badge you soon ✅");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!cert) return null;
  const doneCount = CHECKLIST.filter((c) => cert.checklist?.[c.key]).length;
  const allDone = doneCount === CHECKLIST.length && cert.id_doc_url;

  const STATUS_BADGE = {
    draft: ["Not submitted", "bg-white/5 text-slate-400"],
    pending: ["Under review", "bg-[#22C55E]/10 text-[#22C55E]"],
    approved: ["FUNZ Certified ✅", "bg-[#06B6D4]/10 text-[#06B6D4]"],
    rejected: ["Needs work", "bg-[#F97316]/10 text-[#F97316]"],
  };
  const [badgeLabel, badgeClass] = STATUS_BADGE[cert.status] || STATUS_BADGE.draft;

  return (
    <section className="rounded-3xl border border-[#06B6D4]/25 bg-[#06B6D4]/[0.04] p-6" data-testid="certification-card">
      <p className="flex flex-wrap items-center gap-3 font-display text-lg font-bold">
        ✅ FUNZ Certified Chef Program
        <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${badgeClass}`} data-testid="cert-status">
          {badgeLabel}
        </span>
      </p>
      <p className="mt-1 text-xs text-slate-400">
        Certified chefs rank first in FUNZI matching and get the badge on every booking card.
      </p>
      <div className="mt-5 space-y-2.5" data-testid="cert-checklist">
        {CHECKLIST.map((c) => (
          <label key={c.key} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/60 px-4 py-3">
            <input
              type="checkbox"
              data-testid={`cert-${c.key}`}
              checked={!!cert.checklist?.[c.key]}
              disabled={["pending", "approved"].includes(cert.status)}
              onChange={(e) => toggleItem(c.key, e.target.checked)}
              className="h-4 w-4 accent-[#06B6D4]"
            />
            <span className="text-sm font-bold">{c.label}</span>
            <span className="ml-auto font-jbmono text-[10px] text-slate-600">{c.hint}</span>
          </label>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          data-testid="cert-upload-id"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || ["pending", "approved"].includes(cert.status)}
          className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold text-slate-200 transition-colors hover:border-[#06B6D4]/60 disabled:opacity-50"
        >
          {uploading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          {cert.id_doc_url ? "ID uploaded ✓" : "Upload ID (jpg/png/pdf)"}
        </button>
        <input ref={fileRef} data-testid="cert-id-file" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={uploadId} />
        {cert.status !== "approved" && (
          <button
            data-testid="cert-submit"
            onClick={submit}
            disabled={submitting || !allDone || cert.status === "pending"}
            className="rounded-full bg-gradient-to-r from-[#06B6D4] to-[#22C55E] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
          >
            {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : cert.status === "pending" ? "Awaiting review…" : "Submit for review"}
          </button>
        )}
        <span className="font-jbmono text-[10px] text-slate-600">{doneCount}/{CHECKLIST.length} training items</span>
      </div>
    </section>
  );
};

const WalletCard = () => {
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chefrider/wallet`, headers());
      setWallet(data);
    } catch {
      setWallet(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const requestPayout = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/chefrider/payouts`, { amount: parseInt(amount, 10) || 0 }, headers());
      toast.success("Payout requested 💸 Admin processes these weekly.");
      setAmount("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!wallet) return null;
  return (
    <section className="rounded-3xl border border-[#22C55E]/25 bg-[#22C55E]/[0.04] p-6" data-testid="wallet-card">
      <p className="font-display text-lg font-bold">💰 Chef Earnings Wallet</p>
      <p className="mt-1 text-xs text-slate-400">FUNZ takes a 15% commission on ChefRider bookings — the rest is yours.</p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Gross", value: `₹${wallet.gross.toLocaleString()}` },
          { label: "FUNZ commission", value: `-₹${wallet.commission.toLocaleString()}` },
          { label: "Available", value: `₹${wallet.available.toLocaleString()}` },
          { label: "Paid out", value: `₹${wallet.paid_out.toLocaleString()}` },
        ].map((s, i) => (
          <div key={s.label} data-testid={`wallet-stat-${i}`} className="rounded-2xl border border-white/10 bg-[#09090B]/60 p-4">
            <p className="font-display text-lg font-extrabold">{s.value}</p>
            <p className="mt-0.5 font-jbmono text-[9px] uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input
          data-testid="payout-amount"
          type="number"
          min="100"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={`Amount (max ₹${wallet.available})`}
          className="h-12 flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#22C55E]/60"
        />
        <button
          data-testid="payout-request"
          onClick={requestPayout}
          disabled={busy || !amount || wallet.available < 100}
          className="rounded-full bg-gradient-to-r from-[#22C55E] to-[#F97316] px-6 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
        >
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Request payout"}
        </button>
      </div>
      {wallet.payouts.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="payout-history">
          {wallet.payouts.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#09090B]/60 px-4 py-2.5">
              <span className="font-jbmono text-xs font-bold text-[#22C55E]">₹{p.amount.toLocaleString()}</span>
              <span className="font-jbmono text-[10px] text-slate-600">{String(p.created_at).slice(0, 10)}</span>
              <span className={`ml-auto rounded-full px-2.5 py-0.5 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                p.status === "paid" ? "bg-[#06B6D4]/10 text-[#06B6D4]" : "bg-[#22C55E]/10 text-[#22C55E]"
              }`}>{p.status}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const HomeChefPanel = () => {
  const masters = useMasters();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ chef_type: "Daily Home Chef", cuisines: [], experience: "", travel_radius: "5 KM", vehicle: "Scooter", certificates: "", slots: [], active: true });
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, b] = await Promise.all([
        axios.get(`${API}/chefrider/profile/mine`, headers()),
        axios.get(`${API}/chefrider/bookings/incoming`, headers()),
      ]);
      setBookings(b.data.bookings);
      if (p.data.profile) {
        setProfile(p.data.profile);
        setForm({
          chef_type: p.data.profile.chef_type,
          cuisines: p.data.profile.cuisines ? p.data.profile.cuisines.split(",").map((s) => s.trim()).filter(Boolean) : [],
          experience: p.data.profile.experience,
          travel_radius: p.data.profile.travel_radius,
          vehicle: "Scooter",
          certificates: "",
          slots: p.data.profile.slots,
          active: true,
        });
      }
    } catch {
      setBookings([]);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/chefrider/profile`,
        { ...form, cuisines: form.cuisines.join(", ") },
        headers()
      );
      toast.success("ChefRider profile live 🍽️ Customers can now book you.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/chefrider/bookings/${id}/status`, { status }, headers());
      toast.success(`Booking ${status}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const upcoming = bookings.filter((b) => ["requested", "accepted"].includes(b.status));

  return (
    <div className="space-y-8" data-testid="homechef-panel">
      <WalletCard />
      <CertificationCard />
      <section className="rounded-3xl border border-[#F97316]/25 bg-[#F97316]/[0.05] p-6">
        <p className="font-display text-lg font-bold">👨‍🍳 ChefRider profile — get booked for home cooking</p>
        <p className="mt-1 text-xs text-slate-400">
          Publish your slots and FUNZI matches you with customers nearby. {profile ? "Your profile is live." : "Set it up once."}
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <PillSelect label="Chef Type" options={masters.chef_types || []} value={form.chef_type} onChange={(v) => setForm({ ...form, chef_type: v })} testid="hc-type" />
          <Chips label="Cuisines" options={masters.cuisines || []} values={form.cuisines} onChange={(v) => setForm({ ...form, cuisines: v })} testid="hc-cuisines" />
          <PillSelect label="Experience" options={masters.experience || []} value={form.experience} onChange={(v) => setForm({ ...form, experience: v })} testid="hc-experience" />
          <PillSelect label="Travel Radius" options={masters.service_radius || []} value={form.travel_radius} onChange={(v) => setForm({ ...form, travel_radius: v })} testid="hc-radius" />
          <div className="sm:col-span-2">
            <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">Availability Slots (tap to toggle)</p>
            <div className="flex flex-wrap gap-2" data-testid="hc-slots">
              {CHEFRIDER_SLOTS.map((s) => {
                const on = form.slots.includes(s);
                return (
                  <button
                    key={s}
                    data-testid={`hc-slot-${s.split(" ")[0]}`}
                    onClick={() => setForm({ ...form, slots: on ? form.slots.filter((x) => x !== s) : [...form.slots, s] })}
                    className={`rounded-full border px-4 py-2 text-xs font-bold transition-all ${
                      on ? "border-[#F97316] bg-[#F97316]/15 text-[#F97316]" : "border-white/10 text-slate-400 hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <button
          data-testid="hc-save"
          onClick={saveProfile}
          disabled={saving || !form.slots.length || !form.cuisines.length}
          className="mt-5 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-7 py-3 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
        >
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : profile ? "Update profile" : "Go live on ChefRider"}
        </button>
        {!form.slots.length && <p className="mt-2 font-jbmono text-[10px] text-[#22C55E]">Pick at least one slot</p>}
      </section>

      <section data-testid="hc-bookings">
        <p className="font-display text-lg font-bold">Home-chef bookings ({upcoming.length} upcoming)</p>
        <div className="mt-4 space-y-3">
          {bookings.length === 0 ? (
            <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500">
              No bookings yet — once your profile is live, FUNZI starts matching you.
            </p>
          ) : (
            bookings.map((b) => (
              <div key={b.id} data-testid={`hc-booking-${b.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm font-bold">{b.package} · {b.guests} guests</p>
                  <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${
                    b.status === "accepted" ? "bg-[#06B6D4]/10 text-[#06B6D4]" :
                    b.status === "completed" ? "bg-[#7C3AED]/15 text-[#A78BFA]" :
                    b.status === "cancelled" ? "bg-white/5 text-slate-500" : "bg-[#22C55E]/10 text-[#22C55E]"
                  }`}>{b.status}</span>
                  <span className="ml-auto font-jbmono text-xs font-bold text-[#22C55E]">₹{b.price.toLocaleString()}</span>
                </div>
                <p className="mt-1.5 font-jbmono text-[11px] text-slate-500">
                  {b.customer_name} · {b.date} · {b.slot} · 📍 {b.address}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.status === "requested" && (
                    <button data-testid={`hc-accept-${b.id.slice(-6)}`} onClick={() => setStatus(b.id, "accepted")} disabled={busyId === b.id} className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
                      Accept
                    </button>
                  )}
                  {b.status === "accepted" && (
                    <button data-testid={`hc-complete-${b.id.slice(-6)}`} onClick={() => setStatus(b.id, "completed")} disabled={busyId === b.id} className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
                      Mark Completed
                    </button>
                  )}
                  {["requested", "accepted"].includes(b.status) && (
                    <button data-testid={`hc-cancel-${b.id.slice(-6)}`} onClick={() => setStatus(b.id, "cancelled")} disabled={busyId === b.id} className="rounded-full border border-white/15 px-5 py-2 text-xs font-bold text-slate-400 hover:text-[#F97316]">
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default function ChefMode() {
  const { user, checked } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/incoming`, headers());
      setOrders(data.orders);
    } catch {
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user, load]);

  const advance = async (orderId, status) => {
    setBusyId(orderId);
    try {
      await axios.patch(`${API}/orders/${orderId}/status`, { status }, headers());
      toast.success(`Order #${orderId.slice(-6)} → ${status}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B]">
        <LoaderCircle className="h-8 w-8 animate-spin text-[#06B6D4]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const active = (orders || []).filter((o) => !["delivered", "cancelled"].includes(o.status));
  const revenue = (orders || []).filter((o) => o.status !== "cancelled").reduce((a, o) => a + o.total, 0);

  return (
    <div data-testid="chef-mode" className="min-h-screen bg-[#09090B] text-white">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#09090B]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6">
          <Link to="/" data-testid="chef-logo" className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display text-base font-extrabold tracking-tight">
              FUN<span className="text-[#06B6D4]">Z</span>
              <span className="ml-2 rounded-full bg-[#F97316]/15 px-2.5 py-0.5 font-jbmono text-[9px] uppercase tracking-widest text-[#F97316]">
                Chef Mode
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-2.5">
            <button
              data-testid="chef-refresh"
              onClick={load}
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-[#06B6D4]/60 hover:text-white"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <Link
              to="/app"
              data-testid="chef-back"
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> My FUNZ
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h1 className="flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          <ChefHat className="h-8 w-8 text-[#F97316]" /> Kitchen Board
        </h1>

        <div className="mt-6 flex gap-2" data-testid="chef-tabs">
          {[
            { id: "orders", label: "🔥 Orders" },
            { id: "menu", label: "🍽️ Menu" },
            { id: "homechef", label: "👨‍🍳 Home Chef" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`chef-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === t.id ? "bg-gradient-to-r from-[#F97316] to-[#22C55E] text-[#09090B]" : "border border-white/10 text-slate-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "menu" ? (
          <div className="mt-8">
            <MenuPanel />
          </div>
        ) : tab === "homechef" ? (
          <div className="mt-8">
            <HomeChefPanel />
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-400">Incoming orders, live. Refreshes every 15 seconds.</p>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { label: "Active now", value: active.length },
                { label: "Total orders", value: (orders || []).length },
                { label: "Revenue", value: `₹${revenue}` },
              ].map((s, i) => (
                <div key={s.label} data-testid={`chef-stat-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
                  <p className="font-display text-2xl font-extrabold">{s.value}</p>
                  <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-4" data-testid="chef-orders">
              {orders === null ? (
                <LoaderCircle className="h-6 w-6 animate-spin text-[#06B6D4]" />
              ) : orders.length === 0 ? (
                <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500">
                  No orders yet — share your menu link around campus 🍔
                </p>
              ) : (
                orders.map((o) => (
                  <div
                    key={o.id}
                    data-testid={`chef-order-${o.id.slice(-6)}`}
                    className={`rounded-3xl border p-5 backdrop-blur-xl transition-colors sm:p-6 ${
                      o.status === "placed" ? "border-[#22C55E]/30 bg-[#22C55E]/[0.04]" : "border-white/10 bg-white/[0.03]"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-jbmono text-sm font-bold text-[#06B6D4]">#{o.id.slice(-6)}</span>
                      <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[o.status]}`}>
                        {o.status}
                      </span>
                      <span className="ml-auto font-jbmono text-[11px] text-slate-500">
                        {String(o.created_at).slice(0, 16).replace("T", " ")}
                      </span>
                    </div>
                    <p className="mt-3 text-sm font-semibold text-white">
                      {o.items.map((i) => `${i.qty}× ${i.name}`).join(" · ")}
                      <span className="ml-2 font-jbmono text-[#22C55E]">₹{o.total}</span>
                    </p>
                    <p className="mt-1.5 font-jbmono text-[11px] text-slate-500">
                      {o.email} · {o.phone} · {o.block}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {NEXT[o.status] && (
                        <button
                          data-testid={`chef-advance-${o.id.slice(-6)}`}
                          onClick={() => advance(o.id, NEXT[o.status].to)}
                          disabled={busyId === o.id}
                          className="rounded-full bg-gradient-to-r from-[#F97316] to-[#22C55E] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50"
                        >
                          {busyId === o.id ? "…" : NEXT[o.status].label}
                        </button>
                      )}
                      {!["delivered", "cancelled"].includes(o.status) && (
                        <button
                          data-testid={`chef-cancel-${o.id.slice(-6)}`}
                          onClick={() => advance(o.id, "cancelled")}
                          disabled={busyId === o.id}
                          className="rounded-full border border-white/15 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-slate-400 transition-colors hover:border-[#F97316]/60 hover:text-[#F97316]"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
